import { useCallback, useEffect, useRef } from 'react';
import { useProjectId } from 'store/project';
import { isGridFilterModel } from 'components/common/dataGrid/filterGuards';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { DataGridInstanceName } from 'services/storage/keys';
import {
  getUserFiltersCount,
} from '../models/filterModel/filterModel';
import { useIssueStore } from '../store';
import { useProjectChangeEffect } from 'store/useProjectChangeEffect';

/**
 * Unlike other entity initializers (sites, levels, etc.) that load the whole
 * collection on project change (useEntityCollectionInitializer), issues are fetched on-demand via useGetAllFilteredIssues.
 * Reset clears old data; setProjectId triggers re-render in consumers which fetch fresh data.
 */
export function useIssuesInitializer(): void {
  const projectId = useProjectId();
  const setProjectId = useIssueStore.use.setProjectId();
  const setFilterModel = useIssueStore.use.setFilterModel();
  const filterModel = useIssueStore.use.filterModel();
  const reset = useIssueStore.use.reset();
  const restoredSavedFiltersProjectIdRef = useRef<string | undefined>(undefined);

  useProjectChangeEffect(projectId, {
    onProjectChange: useCallback(() => {
      reset();
    }, [reset]),
  });

  useEffect(() => {
    if (projectId) {
      setProjectId(projectId);
    }
  }, [projectId, setProjectId]);

  useEffect(() => {
    if (!projectId) {
      restoredSavedFiltersProjectIdRef.current = undefined;
      return;
    }

    // Restore saved issue-grid filters only once per project.
    // Rehydrating on every "empty filters" state would override explicit
    // filter clears done in other views (e.g. documentation map preview).
    if (restoredSavedFiltersProjectIdRef.current === projectId) {
      return;
    }

    if (getUserFiltersCount(filterModel) > 0) {
      return;
    }
    restoredSavedFiltersProjectIdRef.current = projectId;

    const savedState = LocalStorageManager.getDataGridState(
      DataGridInstanceName.issue
    );
    const savedFilterModel = savedState?.filter?.filterModel;

    if (
      isGridFilterModel(savedFilterModel) &&
      getUserFiltersCount(savedFilterModel) > 0
    ) {
      setFilterModel(savedFilterModel);
    }
  }, [projectId, filterModel, setFilterModel]);

  useEffect(() => {
    return () => {
      useIssueStore.getState().setProjectId(undefined);
    };
  }, []);
}
